import React, { ReactElement, useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import enumStrings, { RoutePaths } from "../../../enumStrings";
import { commonCognitoPasswordRegex } from "../../common/utilities/commonPasswordRegex";
import PasswordField from "../../common/PasswordField/PasswordField";
import {
  UserType,
  useSetHasSignedUpForChamberMemberMutation,
} from "../../../graphql/types";
import {
  confirmSignIn,
  fetchAuthSession,
  signOut,
} from "aws-amplify/auth";
import BaseAuthPage from "./BaseAuthPage";
import { commonStyles } from "../../common/commonStyles";
import LinkText from "../../common/LinkText";
import Button from "../../common/Button";
import {
  clearPendingNewPasswordChallenge,
  getPendingNewPasswordChallenge,
} from "./pendingNewPasswordChallenge";
import { getSingleDashboardUserType } from "./dashboardUserType";
import { getApolloContext } from "@apollo/client";
import { completeDashboardSignIn } from "./dashboardSignIn";
import { getAuthErrorName, getSignInErrorMessage } from "./authErrors";
import AuthErrorMessage from "./AuthErrorMessage";

// No pending temporary-password challenge: the page was opened directly,
// reloaded, or the challenge timed out. The Set Password button stays off.
const NO_PENDING_CHALLENGE_TEXT =
  "Your session to set a new password has ended. Select Back to sign in, then sign in again with your temporary password.";

interface NewPasswordInput {
  newPassword: string;
  confirmPassword: string;
}

const NewPassword = (): ReactElement => {
  const apolloClient = useContext(getApolloContext());
  const initialValues: NewPasswordInput = {
    newPassword: "",
    confirmPassword: "",
  };

  const validationSchema = yup.object({
    newPassword: yup
      .string()
      .required(enumStrings.pages.common.validation.newPassword)
      .matches(
        commonCognitoPasswordRegex,
        enumStrings.pages.common.validation.mustContain8Characters
      ),
    confirmPassword: yup
      .string()
      .oneOf(
        [yup.ref("newPassword"), undefined],
        enumStrings.pages.common.validation.passwordsMustMatch
      ),
  });

  const [customError, setCustomError] = useState("");
  const pendingChallenge = getPendingNewPasswordChallenge();
  const [loader, setLoader] = useState(false);
  const [challengeCommitted, setChallengeCommitted] = useState(false);

  const [setHasSignedUpForChamberMember] =
    useSetHasSignedUpForChamberMemberMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (input: NewPasswordInput) => {
      setCustomError("");
      let passwordWasCommitted = false;

      if (!pendingChallenge) {
        setCustomError(NO_PENDING_CHALLENGE_TEXT);
        return;
      }

      setLoader(true);
      try {
        const result = await confirmSignIn({
          challengeResponse: input.newPassword,
        });

        if (result.nextStep.signInStep !== "DONE") {
          const challengeError = new Error(
            "The new password challenge was not completed."
          );
          challengeError.name = "NewPasswordChallengeIncomplete";
          throw challengeError;
        }

        clearPendingNewPasswordChallenge();
        setChallengeCommitted(true);
        passwordWasCommitted = true;
        const authSession = await fetchAuthSession();

        if (authSession?.tokens?.accessToken?.payload) {
          const userType = getSingleDashboardUserType(
            authSession.tokens.accessToken.payload as Record<string, unknown>
          );
          if (!userType) {
            throw new Error("The signed-in account has conflicting access.");
          }
          const customAccessTokenForSettingHasSignedUp =
            authSession.tokens.accessToken.toString();

          if (userType === UserType.ChamberMemberAdmin) {
            await setHasSignedUpForChamberMember({
              context: {
                customAccessTokenForSettingHasSignedUp,
              },
            });
          }
        }

        await completeDashboardSignIn(apolloClient);
      } catch (error: unknown) {
        if (passwordWasCommitted) {
          try {
            await signOut();
          } catch (_signOutError) {
            // The backend still validates the resulting session on every
            // request. Always show the recovery guidance even if local
            // sign-out cleanup cannot complete.
          }
          const errorName = getAuthErrorName(error);
          setCustomError(
            errorName === "MemberDashboardRequired" ||
            errorName === "UserDisabled" ||
            errorName === "UserDoesNotHaveAccessToChamberMemberPro"
              ? `Your new password is saved. ${getSignInErrorMessage(error)}`
              : "Your new password is saved. Select Back to sign in, then sign in with your new password."
          );
          return;
        }

        const errorName = getAuthErrorName(error);
        if (errorName === "NetworkError") {
          setCustomError(enumStrings.pages.common.errors.networkError);
        } else if (
          errorName === "NotAuthorizedException" ||
          errorName === "UserNotFoundException" ||
          errorName === "NewPasswordChallengeIncomplete"
        ) {
          clearPendingNewPasswordChallenge();
          setCustomError(NO_PENDING_CHALLENGE_TEXT);
        } else {
          setCustomError(
            `We could not set your password. ${enumStrings.pages.common.validation.mustContain8Characters} If it keeps happening, contact your chamber administrator.`
          );
        }
      } finally {
        setLoader(false);
      }
    },
  });

  return (
    <BaseAuthPage logoSubtitle={enumStrings.pages.newPassword.logoSubtitle}>
      <form
        style={{
          width: "100%",
        }}
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: commonStyles.fontSizes.medium,
              pb: 2,
            }}
          >
            {enumStrings.pages.newPassword.description}
          </Typography>
          <PasswordField
            id="newPassword"
            fieldName="newPassword"
            label="New Password"
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            error={formik.errors.newPassword}
            touched={formik.touched.newPassword}
          />
          <PasswordField
            id="confirmPassword"
            fieldName="confirmPassword"
            label="Confirm Password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            error={formik.errors.confirmPassword}
            touched={formik.touched.confirmPassword}
          />
          <AuthErrorMessage
            message={
              customError ||
              (!pendingChallenge && !challengeCommitted
                ? NO_PENDING_CHALLENGE_TEXT
                : "")
            }
          />
          <Button
            text={enumStrings.pages.newPassword.buttonText}
            disabled={!pendingChallenge || challengeCommitted || loader}
            isLoading={loader}
            fullWidth
            size="large"
            type="submit"
            additionalSx={{
              mt: 4,
            }}
          />
          <LinkText
            to={RoutePaths.login}
            text={enumStrings.pages.common.buttons.goBackToLoginText}
            onClick={() => {
              clearPendingNewPasswordChallenge();
              void signOut();
            }}
          />
        </Box>
      </form>
    </BaseAuthPage>
  );
};

export default NewPassword;
